import { useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { createCase, ProblemError, uploadDocuments, type DocumentOut } from "@/api";
import { Button } from "@/components/Button";
import { StateMessage } from "@/components/StateMessage";
import { formatBytes } from "@/lib/format";
import { isMail } from "@/lib/mail";
import { startReading } from "@/lib/startReading";
import { useDemoRun } from "@/store/demoRun";

import styles from "./DropArea.module.css";

const ACCEPTED = "application/pdf,image/jpeg,image/png";
/** What the picker offers when a mail has somewhere to go (`onMail`). */
const ACCEPTED_WITH_MAIL = `${ACCEPTED},.eml,.msg`;

/**
 * What starts a run — the four suffixes `demo.py`'s own `UPLOAD_MEDIA_TYPES`
 * accepts, and nothing else.
 *
 * A courtesy guard, not the authority: the backend refuses the same files
 * again, and it also compares the declared type against the suffix, which is
 * the check that catches a renamed file. This one exists so the refusal
 * happens before anything is transmitted (design 12.08.2026 §2.1) — the
 * first stumme Wächter costs no round trip and no byte.
 */
const ACCEPTED_SUFFIXES = [".pdf", ".jpg", ".jpeg", ".png"];

export function isDrawing(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_SUFFIXES.some((suffix) => name.endsWith(suffix));
}

/** Names the files that will not be sent, the way a person would say it. */
function refusalOf(files: File[]): string {
  const names = files.map((file) => file.name).join(", ");
  return files.length === 1
    ? `${names} ist keine Zeichnung — PDF, JPEG oder PNG.`
    : `${names} sind keine Zeichnungen — PDF, JPEG oder PNG.`;
}

/**
 * What a drop *is*, for the sole purpose of telling a retry of the same drop
 * apart from a fresh one with different files (Fix round 2, Critical 1). Not
 * a content hash — name, size and the browser's own `lastModified` are
 * enough to tell "the same drop again" from "a different one", and cheap
 * enough to compute on every drop without reading a byte.
 */
function dropFingerprint(files: File[]): string {
  return files.map((file) => `${file.name}|${file.size}|${file.lastModified}`).join(",");
}

/**
 * The one sentence a person gets for a failed drop (PF-5). `ProblemError`'s
 * own `.message` is the problem's **title** — a German status word, not the
 * server's actual refusal — so a real 422 ("Höchstens 4 Seiten je Auslese …")
 * or the retry's 409 would otherwise never reach the screen. `.userMessage`
 * is the sentence to show; same pattern as `features/pricing/ApprovalPanel.tsx`.
 */
function messageOf(cause: unknown): string {
  return cause instanceof ProblemError
    ? cause.userMessage
    : cause instanceof Error
      ? cause.message
      : String(cause);
}

interface DropAreaProps {
  /**
   * What stands above the note — kicker, title, lead. Rendered **inside** the
   * `data-drop-area` root, because the root is what carries the drag handlers:
   * a drawing let go over the headline has to land, and design §6 says the
   * whole area is the target, not a box within it.
   */
  heading?: ReactNode;
  /**
   * A band instead of a field (UX gate round 1, #1): the dashed invitation
   * becomes one row — the words and the button side by side — under a list
   * on the start page. The note above it stays word for word; it is the
   * announcement before a transmission, not decoration (design §2.1).
   */
  compact?: boolean;
  /**
   * Where a customer's mail goes (`useMailDrop`). A drop that holds a mail —
   * dragged or picked — hands **every** file to it, the drawings that came
   * with the mail included, and starts no reading here: the mail is only
   * filed, and what is read is chosen on the Eingang. Without it a mail is
   * refused as before, and the picker does not offer one.
   */
  onMail?: (files: File[]) => void;
  /**
   * Something else owns the screen — a mail is being filed. Drops are ignored
   * and the picker is off: a drawing let go now would open a second Vorgang
   * beside the one the mail is about to open, and its navigation would pull
   * the person back from wherever they are by then.
   */
  disabled?: boolean;
}

/**
 * The start page's drop zone (design 2026-09-23 §3.6): the drawing is stored
 * on a new Vorgang and queued for the worker, and the screen moves on to the
 * reading's own progress page — which survives a reload, because it is a
 * route and not this component's state.
 *
 * Nothing here writes a Stand. The reading is a proposal; a person takes it
 * over on the schema screen (A-014, B2, `TakeOver`).
 *
 * A retry of the **same** drop reuses the Vorgang and the stored documents —
 * uploading the same bytes again would be refused as a duplicate (409). A
 * **different** drop starts over: the store is reset and the previous drop's
 * case id and document ids are dropped before anything else runs.
 */
export function DropArea({ heading, compact = false, onMail, disabled = false }: DropAreaProps) {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  /** What the guard turned away, until the next drop. `null` while nothing was. */
  const [refusal, setRefusal] = useState<string | null>(null);
  /** What `createCase`, `uploadDocuments` or `startExtraction` had to say. */
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The Vorgang `createCase` returned, kept across a failed retry of the
   * *same* drop — a retry that fails after the case already exists must not
   * create a second one.
   */
  const [createdCaseId, setCreatedCaseId] = useState<string | null>(null);
  /** The documents `uploadDocuments` returned, kept the same way. */
  const [uploaded, setUploaded] = useState<DocumentOut[] | null>(null);
  /** The drop `createdCaseId`/`uploaded` belong to — see above. */
  const [createdFor, setCreatedFor] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * A drop, from either entrance — the drag or the picker behind the button.
   *
   * Both entry paths create the case first, store the documents on it, then
   * queue the reading (design §3.6): a Vorgang exists (`createCase`) before
   * anything is transmitted, the drawing is stored (`uploadDocuments`) before
   * it is read, and the worker takes it from there — this component's own
   * job ends at `startExtraction`. Nothing here writes a Stand and nothing
   * here waits for the reading to finish; the progress page does both.
   * With more than one page dropped it ends at the upload: the reading's
   * screen asks which page is read (Seitenwahl, 13.10.2026).
   *
   * A retry of the *same* drop after a failure reuses the Vorgang and the
   * stored documents rather than creating or uploading them a second time —
   * uploading the same bytes again would collide on the same sha256 and be
   * refused as a duplicate. A **different** drop is not a retry:
   * `createdFor` — a fingerprint of the previous drop's files — is compared
   * against this one's, and a mismatch resets the store and drops the
   * cached ids before anything else runs.
   */
  async function receive(incoming: FileList | null): Promise<void> {
    const dropped = incoming ? Array.from(incoming) : [];
    if (dropped.length === 0) return;

    // A mail anywhere in the drop: the whole drop is the mail's, drawings and
    // all (one Vorgang, one transaction). Whatever this area showed of an
    // earlier drawing drop is over.
    if (onMail && dropped.some(isMail)) {
      setFiles([]);
      setRefusal(null);
      setUploadError(null);
      onMail(dropped);
      return;
    }

    // Refused as a whole, never in part: the backend guards every file before
    // it runs any of them, and starting on the readable half of a drop would
    // read a document nobody handed over.
    const refused = dropped.filter((file) => !isDrawing(file));
    if (refused.length > 0) {
      setFiles([]);
      setRefusal(refusalOf(refused));
      return;
    }

    const fingerprint = dropFingerprint(dropped);
    const sameDropAsBefore = fingerprint === createdFor;

    setRefusal(null);
    setUploadError(null);
    setFiles(dropped);
    setBusy(true);
    try {
      let caseId = sameDropAsBefore ? createdCaseId : null;
      let documents = sameDropAsBefore ? uploaded : null;
      if (!sameDropAsBefore) {
        useDemoRun.getState().reset();
        setCreatedFor(fingerprint);
        setCreatedCaseId(null);
        setUploaded(null);
      }
      if (caseId === null) {
        const created = await createCase({
          title: dropped[0]!.name,
          sourceChannel: "upload",
          gratingType: null,
        });
        caseId = created.id;
        setCreatedCaseId(caseId);
      }
      if (documents === null) {
        documents = await uploadDocuments(caseId, dropped);
        setUploaded(documents);
      }
      // More than one page dropped (Seitenwahl, 13.10.2026): the reading
      // reads one, and a person says which on the reading's own screen —
      // nothing is read until then, not even page 1 (`startReading`).
      await startReading(caseId, documents);
      navigate(`/vorgang/${caseId}/einlesen`);
    } catch (cause) {
      setUploadError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`${styles.area} ${dragging ? styles.areaActive : ""}`}
      data-drop-area
      onDragOver={(event) => {
        event.preventDefault();
        if (!busy && !disabled) setDragging(true);
      }}
      onDragLeave={(event) => {
        // Crossing into a child fires `dragleave` on the way in as well, and
        // the whole panel is one target now — so only a boundary that leads
        // out of the root counts as leaving.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        // A drop already in flight owns the screen; a second one on top of
        // it would leave two Vorgänge and one place to show them.
        if (busy || disabled) return;
        void receive(event.dataTransfer.files);
      }}
    >
      {heading}

      {/* Above the zone, not below it: after the drop the transmission has
          already happened, and an announcement that arrives then is not an
          announcement (design §2.1). */}
      <p className={styles.note}>
        Eine Zeichnung startet die Auslese, bei mehreren Seiten nach deiner Wahl, und wird dafür an den Anbieter
        übertragen. Eine Mail wird nur abgelegt — übertragen wird nur, was du im Eingang als Zeichnung wählst.
      </p>

      {/* The dashed box is the *invitation*, not the target. It says where to
          aim; the panel around it accepts the file wherever it is let go. */}
      <div
        className={`${styles.drop} ${compact ? styles.dropCompact : ""} ${dragging ? styles.dropActive : ""} ${
          busy ? styles.dropBusy : ""
        }`}
        aria-busy={busy}
      >
        <p className={styles.dropText}>{busy ? "Wird hochgeladen …" : "Dateien hierher ziehen"}</p>
        <Button variant="secondary" onClick={() => inputRef.current?.click()} disabled={busy || disabled}>
          Dateien auswählen
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={onMail ? ACCEPTED_WITH_MAIL : ACCEPTED}
          aria-label="Dateien auswählen"
          disabled={busy || disabled}
          className="visually-hidden"
          onChange={(event) => {
            const input = event.target;
            // `receive` takes its copy of the list before its first await, so
            // the field can be emptied right after: picking the very same file
            // again — after a refusal, say — fires `change` once more instead
            // of nothing.
            void receive(input.files);
            input.value = "";
          }}
        />
      </div>

      {files.length > 0 ? (
        // What went over, not a queue: the drop started the upload, so there
        // is nothing here to remove any more.
        <ul className={styles.fileList}>
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className={styles.file}>
              <span className={styles.fileName}>{file.name}</span>
              <span className={`tabular ${styles.fileMeta}`}>{formatBytes(file.size)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {refusal || uploadError ? (
        <div className={styles.notice}>
          {refusal ? (
            <StateMessage tone="conflict" title="Nichts wurde übertragen.">
              {refusal}
            </StateMessage>
          ) : (
            <StateMessage tone="conflict" title="Die Auslese hat nicht begonnen.">
              {uploadError}
            </StateMessage>
          )}
        </div>
      ) : null}
    </div>
  );
}
